<template>
  <button v-if="buttonStyle == 0" type="button" class="btn btn-primary" @click="showModal()"><font-awesome-icon icon="plus" /> Neuer Benutzer</button>

  <button v-else-if="buttonStyle == 1" type="button" class="btn btn-primary" @click="showModal()">
    <font-awesome-icon icon="pen" class="me-2" /> Benutzer bearbeiten
  </button>

  <button v-else type="button" class="btn btn-primary btn-sm" title="Benutzer bearbeiten" @click="showModal()">
    <font-awesome-icon icon="pen" />
  </button>

  <!-- Edit BenutzerModal -->
  <div class="modal fade" :id="'editUserModal' + index" tabindex="-1" :aria-labelledby="'editUserModal' + index" aria-hidden="true" ref="editUserModalRef">
    <div class="modal-dialog modal-lg">
      <div v-if="editBenutzer != undefined" class="modal-content">
        <div class="modal-header bg-primary py-2" data-bs-theme="dark">
          <span v-if="editBenutzer.id == ''" class="modal-title my-auto text-white" :id="'editUserModalLabel' + index">Neuer Benutzer</span>

          <span v-else class="modal-title my-auto text-white" :id="'editUserModalLabel' + index">Benutzer bearbeiten</span>

          <button type="button" class="btn-close btn-sm" @click="closeModal()"></button>
        </div>

        <div class="modal-body" :id="'editUserModalBody' + index">
          <LoadingSpinner v-if="loading" />

          <form v-else @submit.prevent="saveBenutzer">
            <div class="form-check mb-3">
              <input class="form-check-input" type="checkbox" v-model="editBenutzer.enabled" :id="'benutzerEnabled-' + index" />
              <label class="form-check-label" :for="'benutzerEnabled-' + index"> Aktiv </label>
            </div>

            <div class="mb-3">
              <label :for="'benutzerFirstName-' + index" class="form-label fw-bold">Vorname</label>
              <input type="text" class="form-control" :id="'benutzerFirstName-' + index" v-model="editBenutzer.firstName" autocomplete="given-name" />
            </div>

            <div class="mb-3">
              <label :for="'benutzerLastName-' + index" class="form-label fw-bold">Nachname</label>
              <input type="text" class="form-control" :id="'benutzerLastName-' + index" v-model="editBenutzer.lastName" autocomplete="family-name" />
            </div>

            <div class="mb-3">
              <label :for="'benutzerUsername-' + index" class="form-label fw-bold">Username</label>
              <input
                type="text"
                class="form-control"
                :id="'benutzerUsername-' + index"
                v-model="editBenutzer.username"
                :disabled="editBenutzer.id != ''"
                autocomplete="username"
              />
            </div>

            <div class="mb-1">
              <label :for="'benutzerEmail-' + index" class="form-label fw-bold">E-Mail</label>
              <input type="text" class="form-control" :id="'benutzerEmail-' + index" v-model="editBenutzer.email" autocomplete="email" />
            </div>

            <div class="form-check mb-3">
              <input class="form-check-input" type="checkbox" v-model="editBenutzer.emailVerified" :id="'benutzerEmailVerified-' + index" />
              <label class="form-check-label" :for="'benutzerEmailVerified-' + index"> E-Mail verifiziert </label>
            </div>

            <div v-if="editBenutzer.id == ''" class="mb-1">
              <label :for="'benutzerPassword-' + index" class="form-label fw-bold">Passwort</label>
              <input type="password" class="form-control" :id="'benutzerPassword-' + index" v-model="password" autocomplete="new-password" />
            </div>

            <hr class="my-3" />

            <div v-if="errorMsg != ''" class="alert alert-danger mb-3">
              Es ist ein Fehler aufgetreten:<br />
              {{ errorMsg }}
            </div>

            <div class="d-flex justify-content-end">
              <button type="button" class="btn btn-secondary me-3" @click="closeModal()">Abbrechen</button>
              <button type="submit" class="btn btn-primary">Speichern</button>
            </div>
          </form>
        </div>

        <div v-if="!loading && editBenutzer.id != ''" class="modal-footer justify-content-start">
          <button type="button" class="btn btn-danger" @click="deleteBenutzer()"><font-awesome-icon icon="trash" /> Benutzer löschen</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
  //#region Imports
  import { PropType, computed, ref } from 'vue';
  import { Modal } from 'bootstrap';
  import { useBenutzerStore } from '@/stores/benutzerStore';
  import { useCommonStore } from '@/stores/commonStore';
  // // import { useAuthStore } from '@/stores/authStore';

  import { EditUserRequest, User } from '@/models/BenutzerModels';

  import LoadingSpinner from '../LoadingSpinner.vue';
  //#endregion

  const props = defineProps({
    user: {
      type: Object as PropType<User>,
      default: new User(),
    },

    realm: {
      type: String as PropType<string>,
      default: 'admin',
    },

    index: {
      type: Number as PropType<number>,
      required: true,
    },

    buttonStyle: {
      type: Number as PropType<number>,
      default: 0, // 0 = erstellen; 1 = bearbeiten
    },
  });

  const emits = defineEmits(['deleteBenutzer', 'savedBenutzer']);

  // const authStore = useAuthStore();
  const benutzerStore = useBenutzerStore();
  const commonStore = useCommonStore();

  const loading = computed(() => commonStore.status.createBenutzerLoading || commonStore.status.updateBenutzerLoading);
  const errorMsg = computed(() => commonStore.status.benutzerErrorMsg);

  const editBenutzer = ref(JSON.parse(JSON.stringify(props.user)) as User);
  const password = ref('');

  function saveBenutzer() {
    if (editBenutzer.value.id == '') {
      editBenutzer.value.username = editBenutzer.value.username.trim();
    }

    editBenutzer.value.email = editBenutzer.value.email.trim();

    editBenutzer.value.firstName = editBenutzer.value.firstName.trim();
    editBenutzer.value.lastName = editBenutzer.value.lastName.trim();

    if (editBenutzer.value.id == '' && password.value.trim() !== '') {
      editBenutzer.value.credentials = [
        {
          type: 'password',
          value: password.value,
          temporary: false,
          id: '',
        },
      ];
    } else {
      editBenutzer.value.credentials = null;
    }

    const request = new EditUserRequest(props.realm, editBenutzer.value);

    if (editBenutzer.value.id !== '') {
      benutzerStore.updateRealmUser(request).then(() => {
        if (errorMsg.value == '') {
          emits('savedBenutzer');
          closeModal();
        }
      });
    } else {
      benutzerStore.createRealmUser(request).then(() => {
        if (errorMsg.value == '') {
          emits('savedBenutzer');
          closeModal();
        }
      });
    }
  }

  function deleteBenutzer() {
    closeModal();
    emits('deleteBenutzer', props.user);
  }

  //#region Modal
  const editUserModalRef = ref();

  function closeModal() {
    Modal.getInstance(editUserModalRef.value)?.hide();
  }

  function showModal() {
    if (editUserModalRef.value) {
      editBenutzer.value = JSON.parse(JSON.stringify(props.user)) as User;
      password.value = '';
      // editBenutzer.value.realm = editBenutzer.value.realm || authStore.kc.realm;

      new Modal(editUserModalRef.value).show();
    }
  }
  //#endregion
</script>

<style scoped>
  .modal-content {
    background: rgb(242, 243, 248);
  }

  /* .modal-body {
  background: rgb(242, 243, 248);
}

.modal-footer {
  background: rgb(242, 243, 248);
} */
</style>
